import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import SEO from '../../components/SEO';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, user } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [location] = useState(() => window.history?.state?.usr || {});

  // Check if demo mode requested
  useEffect(() => {
    if (searchParams?.get('demo') === 'true') {
      setShowDemoCredentials(true);
      setFormData({
        email: 'demo.admin@adubuild.com',
        password: 'demo123'
      });
    }
  }, [searchParams]);

  // Show success message from navigation state (e.g., after accept-invite)
  useEffect(() => {
    const navState = window.history?.state?.usr;
    if (navState?.message) {
      setSuccessMessage(navState?.message);
      if (navState?.email) {
        setFormData(prev => ({ ...prev, email: navState?.email }));
      }
    }
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    
    try {
      await signIn(formData?.email, formData?.password);
      // Navigation will happen automatically via useEffect when user state updates
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific error messages
      const errorMessage = error?.message || '';
      
      if (errorMessage?.includes('Invalid login credentials')) {
        setGeneralError('Invalid email or password. Please try again.');
      } else if (errorMessage?.includes('Email not confirmed')) {
        setGeneralError('Please verify your email address before signing in.');
      } else if (errorMessage?.includes('Too many requests')) {
        setGeneralError('Too many login attempts. Please try again later.');
      } else {
        setGeneralError('An error occurred during sign in. Please try again.');
      }
      
      // Clear form password on error
      setFormData(prev => ({
        ...prev,
        password: ''
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-background flex items-center justify-center p-4">
      <SEO
        title="Log In to ADU Flow — ADU Construction CRM"
        description="Sign in to your ADU Flow account to manage your ADU construction projects, permits, contractors, and customers."
        canonical="/login"
        keywords="ADU Flow login, ADU construction CRM, sign in, project management"
        noIndex={true}
      />
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow logo - ADU Construction CRM"
              className="h-16 w-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
          <p className="text-muted-foreground">
            Sign in to access your ADU Flow
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-md flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        )}

        {/* Demo Credentials Banner */}
        {showDemoCredentials && (
          <div className="mb-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Demo Account Loaded</p>
                <p className="text-sm text-muted-foreground">
                  Email: <span className="font-mono">demo.admin@adubuild.com</span><br />
                  Password: <span className="font-mono">demo123</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Login Form Card */}
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          {/* General Error Message */}
          {generalError && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{generalError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="you@example.com"
              value={formData?.email}
              onChange={handleChange}
              error={errors?.email}
              required
              autoComplete="email"
              disabled={loading}
            />

            {/* Password Input */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                label="Password"
                placeholder="Enter your password"
                value={formData?.password}
                onChange={handleChange}
                error={errors?.password}
                required
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e?.target?.checked)}
                  className="h-4 w-4 rounded border-input bg-background text-accent focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  disabled={loading}
                />
                <span className="text-sm text-muted-foreground">
                  Remember me
                </span>
              </label>
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-accent hover:text-accent/80 transition-colors font-medium"
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              className="mt-6"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link
                to="/sign-up"
                className="text-accent hover:text-accent/80 font-medium transition-colors"
              >
                Create Account
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          © 2026 ADU Flow. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;